import React, {useContext, useEffect, useRef, useState} from 'react';
import {Box, Stack, Typography, IconButton, Menu, MenuItem, CircularProgress, Tooltip, Skeleton, Button} from '@mui/material';
import {useAtom, useAtomValue} from 'jotai';
import {HugeiconsIcon} from '@hugeicons/react';
import {
  ArrowLeft01Icon, Copy01Icon, Delete01Icon, MoreVerticalIcon, PencilEdit01Icon, Refresh01Icon, Key01Icon, DatabaseIcon, LockIcon, LockOpenIcon
} from '@hugeicons/core-free-icons';
import {Context} from '../../../../context/AppContext';
import {MCPToolsSectionNew} from './MCPToolsSectionNew';
import {ConfirmModalNew} from './ConfirmModalNew';
import {EditMCPServerModalNew} from './EditMCPServerModalNew';
import EditTokenModalNew from './EditTokenModalNew';
import {selectedServerAtom, mcpLoadingAtom, flowUidAtom, projectAtom, subProjectAtom} from '../atoms';
import {toast} from 'react-toastify';

const CARD_SHADOW = "0 2px 12px rgba(35, 38, 77, 0.07)";

const SectionHeading = ({title, count}) => (
  <Stack direction="row" alignItems="baseline" gap={1.25} sx={{mb: 1.75}}>
    <Typography component="h3" sx={{fontSize: 20, fontWeight: 700, color: '#23264D', letterSpacing: '-0.02em'}}>{title}</Typography>
    {count !== undefined && (
      <Box component="span" sx={{px: 1.1, py: 0.2, borderRadius: '999px', fontSize: 12, fontWeight: 600, color: '#4B55C9', bgcolor: 'rgba(132, 145, 247, 0.14)'}}>
        {count}
      </Box>
    )}
  </Stack>
);

const ConnectionRow = ({label, action, children}) => (
  <Stack
    direction={{xs: 'column', sm: 'row'}} alignItems={{sm: 'center'}} gap={{xs: 0.75, sm: 2}}
    sx={{px: 2.5, py: 1.75, '&:not(:last-of-type)': {boxShadow: 'inset 0 -1px 0 #F2F2F7'}}}
  >
    <Typography sx={{width: {sm: 150}, flexShrink: 0, fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8A8AA0'}}>
      {label}
    </Typography>
    <Box sx={{flex: 1, minWidth: 0}}>{children}</Box>
    {action}
  </Stack>
);

const rowButtonSx = {
  textTransform: 'none', fontWeight: 600, fontSize: 13, borderRadius: '10px', height: 34, px: 1.5, flexShrink: 0, alignSelf: {xs: 'flex-start', sm: 'center'},
  color: '#374151', bgcolor: '#FAFAFD', '&:hover': {bgcolor: '#ECECF3'},
};

export const MCPServerDetailsPanelNew = () => {
  const [selectedServer, setSelectedServer] = useAtom(selectedServerAtom);
  const loading = useAtomValue(mcpLoadingAtom);
  const flowUid = useAtomValue(flowUidAtom);
  const project = useAtomValue(projectAtom);
  const subProject = useAtomValue(subProjectAtom);
  const mcpScope = {flowUid, projectId: project?.id, subProjectId: subProject?.id};
  const { deleteMCPServer, getAllServersWithDetails, updateMCPServer, reloadMCPServer, editServerToken } = useContext(Context);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [tokenOpen, setTokenOpen] = useState(false);
  const [reloading, setReloading] = useState(false);

  const topRef = useRef(null);
  useEffect(() => {
    topRef.current?.scrollIntoView({block: 'start'});
  }, [selectedServer?.id]);

  if (!selectedServer) return null;

  const toolCount = selectedServer?.tools?.length || 0;
  const isSecure = selectedServer.auth_token === true;

  const handleCopyEndpoint = async () => {
    try {
      await navigator.clipboard.writeText(selectedServer.endpoint);
      toast.success('Endpoint copied');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleDeleteServer = async () => {
    try {
      setDeleting(true);
      await deleteMCPServer(selectedServer.id);
      setSelectedServer(null);
      setConfirmOpen(false);
      if (flowUid) {
        await getAllServersWithDetails(mcpScope);
      }
    } catch (err) {
      toast.error('Failed to delete server');
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const handleEditSave = async (id, data) => {
    const updated = await updateMCPServer(id, data);
    if (updated) setSelectedServer(updated);
  };

  const handleTokenSave = async (newToken) => {
    try {
      await editServerToken(selectedServer.id, newToken);
      toast.success('Token updated');
    } catch (err) {
      toast.error('Failed to update token');
    }
  };

  const refreshSelectedServer = async () => {
    if (!flowUid) return;
    const refreshed = await getAllServersWithDetails(mcpScope);
    const updated = refreshed?.find(s => s.id === selectedServer.id);
    if (updated) setSelectedServer(updated);
  };

  const handleReload = async () => {
    setMenuAnchor(null);
    setReloading(true);
    try {
      await reloadMCPServer(selectedServer);
      await refreshSelectedServer();
    } catch (err) {
      console.error(err);
    } finally {
      setReloading(false);
    }
  };

  return (
    <Box ref={topRef} sx={{pb: 8, scrollMarginTop: '16px'}}>
      {/* Breadcrumb */}
      <Stack direction="row" alignItems="center" gap={1.25} sx={{mb: 2}}>
        <IconButton
          onClick={() => setSelectedServer(null)} aria-label="Back to MCP servers"
          sx={{
            width: 38, height: 38, bgcolor: '#fff', color: '#23264D', boxShadow: '0 2px 12px rgba(35, 38, 77, 0.07)',
            transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), background-color .15s ease',
            '&:hover': {bgcolor: '#fff', transform: 'translateX(-3px)'},
          }}
        >
          <HugeiconsIcon icon={ArrowLeft01Icon} size={18}/>
        </IconButton>
        <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8491F7'}}>MCP servers</Typography>
      </Stack>

      {/* Hero */}
      <Stack
        direction={{xs: 'column', md: 'row'}} alignItems={{xs: 'flex-start', md: 'flex-end'}} gap={2.25}
        sx={{position: 'relative', borderRadius: '24px', p: {xs: 2.5, md: 3.5}, background: 'linear-gradient(120deg, #E9ECFF 0%, #F3E9FA 55%, #FCE9F1 100%)'}}
      >
        <Box sx={{
          width: {xs: 48, md: 64}, height: {xs: 48, md: 64}, borderRadius: {xs: '16px', md: '20px'}, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: '#fff', boxShadow: CARD_SHADOW, color: '#8491F7',
        }}>
          <HugeiconsIcon icon={DatabaseIcon} size={26}/>
        </Box>
        <Box sx={{flex: {md: '1 1 280px'}, width: {xs: '100%', md: 'auto'}, minWidth: 0, pr: {md: 12}}}>
          <Stack direction="row" alignItems="center" gap={0.75} sx={{mb: 0.75}}>
            <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: selectedServer.active ? '#22C55E' : '#C9CAD8'}}/>
            <Typography sx={{fontSize: 12, fontWeight: 600, color: '#6B6B80'}}>{selectedServer.active ? 'Active' : 'Inactive'}</Typography>
          </Stack>
          <Typography component="h2" sx={{fontSize: {xs: 24, md: 32}, fontWeight: 700, color: '#23264D', letterSpacing: '-0.03em', lineHeight: 1.1}}>
            {selectedServer.name}
          </Typography>
          <Stack direction="row" alignItems="center" gap={0.5} sx={{mt: 0.75}}>
            <Typography sx={{fontSize: 12.5, color: '#6B6B80', fontFamily: 'monospace', wordBreak: 'break-all'}}>{selectedServer.endpoint}</Typography>
            <Tooltip title="Copy endpoint">
              <IconButton size="small" onClick={handleCopyEndpoint} sx={{p: 0.25, color: '#8A8AA0', '&:hover': {backgroundColor: 'rgba(255, 255, 255, 0.6)', color: '#4B55C9'}}}>
                <HugeiconsIcon icon={Copy01Icon} size={13}/>
              </IconButton>
            </Tooltip>
          </Stack>
          <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{mt: 1.5}}>
            {[`${toolCount} tool${toolCount === 1 ? '' : 's'}`, isSecure ? 'Secure connection' : 'Insecure connection'].map(label => (
              <Box key={label} component="span" sx={{px: 1.25, py: 0.4, borderRadius: '8px', fontSize: 12, fontWeight: 500, color: '#23264D', bgcolor: 'rgba(255, 255, 255, 0.7)'}}>
                {label}
              </Box>
            ))}
          </Stack>
        </Box>
        <Stack direction="row" alignItems="center" gap={1} sx={{position: 'absolute', top: {xs: 14, md: 20}, right: {xs: 14, md: 20}}}>
          <Tooltip title="Reload tools">
            <span>
              <IconButton onClick={handleReload} disabled={reloading} sx={{bgcolor: 'rgba(255, 255, 255, 0.7)', '&:hover': {bgcolor: '#fff'}}}>
                {reloading ? <CircularProgress size={16} sx={{color: '#8491F7'}}/> : <HugeiconsIcon icon={Refresh01Icon} size={17}/>}
              </IconButton>
            </span>
          </Tooltip>
          <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="More actions" sx={{bgcolor: 'rgba(255, 255, 255, 0.7)', '&:hover': {bgcolor: '#fff'}}}>
            <HugeiconsIcon icon={MoreVerticalIcon} size={18}/>
          </IconButton>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)} PaperProps={{sx: {borderRadius: '12px'}}}>
            <MenuItem onClick={() => {setMenuAnchor(null); setEditOpen(true);}} sx={{gap: 1}}><HugeiconsIcon icon={PencilEdit01Icon} size={16}/><Typography sx={{fontSize: '13px'}}>Edit Server</Typography></MenuItem>
            <MenuItem onClick={() => {setMenuAnchor(null); setTokenOpen(true);}} sx={{gap: 1}}><HugeiconsIcon icon={Key01Icon} size={16}/><Typography sx={{fontSize: '13px'}}>Update Token</Typography></MenuItem>
            <MenuItem onClick={() => {setMenuAnchor(null); handleCopyEndpoint();}} sx={{gap: 1}}><HugeiconsIcon icon={Copy01Icon} size={16}/><Typography sx={{fontSize: '13px'}}>Copy Endpoint</Typography></MenuItem>
            <MenuItem onClick={() => {setMenuAnchor(null); setConfirmOpen(true);}} sx={{color: '#DC2626', gap: 1}}><HugeiconsIcon icon={Delete01Icon} size={16}/><Typography sx={{fontSize: '13px'}}>Delete Server</Typography></MenuItem>
          </Menu>
        </Stack>
      </Stack>

      <Box sx={{height: 28}}/>

      {loading ? (
        <Stack gap={1.25}>
          {Array.from({length: 4}).map((_, index) => (
            <Skeleton key={index} variant="rounded" height={64} sx={{borderRadius: '16px'}}/>
          ))}
        </Stack>
      ) : (
        <Stack gap={5}>
          <Box id="mcp-tools" sx={{scrollMarginTop: '80px'}}>
            <SectionHeading title="Tools" count={toolCount}/>
            <MCPToolsSectionNew server={selectedServer} onToolDeleted={refreshSelectedServer}/>
          </Box>

          <Box id="mcp-connection" sx={{scrollMarginTop: '80px'}}>
            <SectionHeading title="Connection"/>
            <Box sx={{bgcolor: '#fff', borderRadius: '20px', boxShadow: CARD_SHADOW, overflow: 'hidden'}}>
              <ConnectionRow label="Endpoint" action={
                <Button startIcon={<HugeiconsIcon icon={Copy01Icon} size={14}/>} onClick={handleCopyEndpoint} sx={rowButtonSx}>Copy</Button>
              }>
                <Typography sx={{fontFamily: 'monospace', fontSize: 13, color: '#23264D', overflowWrap: 'anywhere'}}>{selectedServer.endpoint}</Typography>
              </ConnectionRow>
              <ConnectionRow label="Security">
                <Stack direction="row" alignItems="center" gap={0.75} sx={{color: isSecure ? '#2F9A6A' : '#D9A13A', fontWeight: 600, fontSize: 13}}>
                  <HugeiconsIcon icon={isSecure ? LockIcon : LockOpenIcon} size={15}/>
                  {isSecure ? 'Connection is secure' : 'Connection may not be secure'}
                </Stack>
              </ConnectionRow>
              <ConnectionRow label="Auth token" action={
                <Button startIcon={<HugeiconsIcon icon={Key01Icon} size={14}/>} onClick={() => setTokenOpen(true)} sx={rowButtonSx}>Update token</Button>
              }>
                <Typography sx={{fontFamily: 'monospace', fontSize: 13, color: '#8A8AA0', letterSpacing: '0.15em'}}>••••••••••••</Typography>
              </ConnectionRow>
              <ConnectionRow label="Server settings" action={
                <Button startIcon={<HugeiconsIcon icon={PencilEdit01Icon} size={14}/>} onClick={() => setEditOpen(true)} sx={rowButtonSx}>Edit server</Button>
              }>
                <Typography sx={{fontSize: 13, color: '#6B6B80'}}>Change the name and endpoint of this server.</Typography>
              </ConnectionRow>
            </Box>

            <Stack
              direction={{xs: 'column', sm: 'row'}} justifyContent="space-between" alignItems={{sm: 'center'}} gap={1.5}
              sx={{mt: 2, p: 2.25, borderRadius: '16px', bgcolor: 'rgba(220, 38, 38, 0.05)'}}
            >
              <Box>
                <Typography sx={{fontSize: 14, fontWeight: 700, color: '#B42318'}}>Delete server</Typography>
                <Typography sx={{fontSize: 13, color: '#8A8AA0', mt: 0.25}}>
                  Permanently removes this server with its registered tools and prompts.
                </Typography>
              </Box>
              <Button
                startIcon={<HugeiconsIcon icon={Delete01Icon} size={15}/>} onClick={() => setConfirmOpen(true)}
                sx={{
                  flexShrink: 0, textTransform: 'none', fontWeight: 600, fontSize: 13, borderRadius: '10px', height: 38,
                  color: '#B42318', bgcolor: 'rgba(220, 38, 38, 0.08)', '&:hover': {bgcolor: 'rgba(220, 38, 38, 0.14)'},
                }}
              >
                Delete server
              </Button>
            </Stack>
          </Box>
        </Stack>
      )}

      <ConfirmModalNew
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDeleteServer}
        title={`Delete "${selectedServer.name}"?`}
        description="This will permanently remove the server and its registered tools and prompts. This action cannot be undone."
        confirmText="Delete Server"
        loading={deleting}
      />

      <EditMCPServerModalNew
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSave={handleEditSave}
        serverData={selectedServer}
      />

      <EditTokenModalNew
        open={tokenOpen}
        onClose={() => setTokenOpen(false)}
        onSave={handleTokenSave}
        server={selectedServer}
      />
    </Box>
  );
};
